import { Navigate } from "react-router-dom";
import { StorageGetItem, STORAGE_KEYS } from "./StorageComp";
import Unauthorized from "./401";
import AddonRequired, { ServiceUnavailable } from "./AddonRequired";
import { hasFeature, isSuspended } from "./Features";

export const authData = () => {
  const authData = StorageGetItem(STORAGE_KEYS.AUTH);
  if (authData) {
    return authData;
  } else {
    return null;
  }
}

export const authCustomerData = () => {
  const authData = StorageGetItem(STORAGE_KEYS.AUTH_CUSTOMER);
  if (authData) {
    return authData;
  } else {
    return null;
  }
}

export const isAuthenticate = () => {
  const authData = StorageGetItem(STORAGE_KEYS.AUTH);
  if (authData) {
    return true;
  } else {
    return false;
  }
}

const currentHost = () => {
  let currentDomain = window.location.hostname;
  if (window.location.port) {
    currentDomain += ":" + window.location.port;
  }
  return currentDomain;
}

// Pages every signed-in company user may open, whatever their role or billing state
const OPEN_PATHS = ["/my-profile", "/company-profile", "/account-suspended"];

export const withAdminAuth = (WrappedComponent) => {
  return (props) => {
    if (typeof window !== "undefined") {
      const authData = StorageGetItem(STORAGE_KEYS.AUTH);

      if (authData) {
        const currentPath = window.location.pathname;
        const currentDomain = currentHost();

        if (currentDomain !== process.env.REACT_APP_BASE_DOMAIN && currentDomain !== authData?.company?.domain) {
          return <Navigate to={"/sign-in"} replace />;
        }

        if (authData?.user_type === 3) {
          return <Navigate to={"/driver/today-request-list"} />;
        }

        if (authData?.user_type === 1) {
          // The platform admin has its own dashboard
          if (currentPath === "/dashboard") {
            return <Navigate to={"/super-admin/dashboard"} replace />;
          }
          return <WrappedComponent {...props} />;
        }

        // Overdue invoice past the grace period: only billing and profile pages stay open
        if (isSuspended(authData) && OPEN_PATHS.indexOf(currentPath) === -1) {
          return <Navigate to={"/account-suspended"} replace />;
        }

        if (props?.feature && !hasFeature(props.feature, authData)) {
          return <AddonRequired feature={props.feature} />;
        }

        if (currentPath === "/dashboard" || OPEN_PATHS.indexOf(currentPath) > -1) {
          return <WrappedComponent {...props} />;
        }

        const isPagePerm = authData?.permissions?.indexOf(props?.permission) > -1;
        return authData?.user_type === 2 || isPagePerm ? (<WrappedComponent {...props} />) : (<Unauthorized />);
      } else {
        return <Navigate to={"/sign-in"} replace />;
      }
    }
    return null;
  };
}

// Platform pages: main domain and super admin only
export const withSuperAdminAuth = (WrappedComponent) => {
  return (props) => {
    if (typeof window !== "undefined") {
      const authData = StorageGetItem(STORAGE_KEYS.AUTH);

      if (!authData) {
        return <Navigate to={"/sign-in"} replace />;
      }
      if (authData?.user_type !== 1 || currentHost() !== process.env.REACT_APP_BASE_DOMAIN) {
        return <Unauthorized />;
      }
      return <WrappedComponent {...props} />;
    }
    return null;
  };
}

export const withCustomerAuth = (WrappedComponent) => {
  return (props) => {
    if (typeof window !== "undefined") {
      const authData = StorageGetItem(STORAGE_KEYS.AUTH);

      if (authData) {
        let currentDomain = window.location.hostname;

        if (window.location.port) {
          currentDomain += ":" + window.location.port;
        }

        if (currentDomain !== process.env.REACT_APP_BASE_DOMAIN && currentDomain !== authData?.company?.domain) {
          return <Navigate to={"/sign-in"} replace />;
        }

        if (authData && (authData?.user_type === 5)) {
          if (isSuspended(authData)) {
            return <ServiceUnavailable />;
          }
          if (props?.feature && !hasFeature(props.feature, authData)) {
            return <ServiceUnavailable feature={props.feature} />;
          }
          return <WrappedComponent {...props} />;
        } else {
          return <Navigate to={"/sign-in"} />;
        }
      } else {
        return <Navigate to={"/sign-in"} replace />;
      }
    }
    return null;
  };
}

export const getCompanySlug = () => {
  const authData = StorageGetItem(STORAGE_KEYS.AUTH);
  let companySlugTemp = 'app';

  if (authData) {
    let auth = JSON.parse(authData);

    if (auth?.company) {
      if (auth?.company?.url_token) {
        if (auth?.company?.url_token !== '') {
          companySlugTemp = auth?.company?.url_token;
        }
      }
    }
  }

  return companySlugTemp;
}